<script lang="ts">
import { page } from "$app/state";
import { goto } from "$app/navigation";
import { tick, onMount } from "svelte";

import {
  Globe,
  House,
  User,
  FolderKanban,
  FileText,
  Mail,
  Link as LinkIcon,
  Scale,
  Cookie,
  PanelLeft,
} from "@lucide/svelte";
import Dot from "$lib/components/Dot.svelte";
import * as Command from "$lib/components/ui/command";
import { Kbd, KbdGroup } from "$lib/components/ui/kbd";
import { languages, type Language } from "$lib/lang";
import {
  getLocalizedUrl,
  isActive,
  t,
  locale,
  setLocale as updateStoreLocale,
} from "$lib/i18n";
import { cn } from "$lib/utils";
import { useSidebar } from "$lib/components/ui/sidebar";
import { createWebHaptics } from "web-haptics/svelte";
import { onDestroy } from "svelte";
import { type LocaleCode } from "$lib/i18n/helpers";
import LocalizedLink from "../LocalizedLink.svelte";
const { trigger, destroy } = createWebHaptics();
onDestroy(destroy);

const sidebar = useSidebar();

function setLocale(newLocale: string) {
  updateStoreLocale(newLocale);
  const localizedPath = getLocalizedUrl(
    page.url.pathname,
    newLocale as Language["code"],
  );
  goto(localizedPath, { invalidateAll: true });
}

let open = $state(false);
let isMac = $state(false);

const hapticPattern = [
  { duration: 60, intensity: 1 },
  { delay: 30, duration: 60, intensity: 0.75 },
  { delay: 30, duration: 60 },
  { delay: 30, duration: 60, intensity: 0.75 },
  { delay: 30, duration: 60, intensity: 1 },
];

const navigationLinks = [
  { href: "/", label: () => $t("sites.root"), icon: House },
  { href: "/aboutme", label: () => $t("sites.aboutme"), icon: User },
  {
    href: "/projects",
    label: () => $t("sites.projects"),
    icon: FolderKanban,
  },
  {
    href: "/cv",
    href2: "/cv/ats",
    label: () => $t("sites.cv"),
    icon: FileText,
  },
  { href: "/contact", label: () => $t("sites.contact"), icon: Mail },
  {
    href: "/linkhub",
    label: () => $t("sites.linkhub"),
    icon: LinkIcon,
  },
  {
    href: "/imprint",
    label: () => $t("sites.imprint"),
    icon: Scale,
  },
];

function navigateTo(href: string) {
  open = false;
  trigger(hapticPattern);
  const localizedHref = getLocalizedUrl(href, $locale as LocaleCode);
  goto(localizedHref);
}

async function selectLanguage(lang: Language) {
  open = false;
  trigger(hapticPattern);
  await tick();
  setLocale(lang.code);
}

function handleCookieSettings() {
  open = false;
  trigger(hapticPattern);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("show-cookie-consent"));
  }
}

function toggleSidebar() {
  open = false;
  trigger(hapticPattern);
  sidebar.toggle();
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === "j" && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    open = !open;
    return;
  }

  if (!open) return;

  const num = Number.parseInt(e.key, 10);
  if (num >= 1 && num <= navigationLinks.length) {
    e.preventDefault();
    navigateTo(navigationLinks[num - 1].href);
  }
}

onMount(() => {
  isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  function onToggle() {
    open = !open;
  }
  window.addEventListener("toggle-command-menu", onToggle);
  return () => {
    window.removeEventListener("toggle-command-menu", onToggle);
  };
});
</script>

<svelte:document onkeydown={handleKeydown} />

<Command.Dialog
  bind:open
  title={$t("commandMenu.commandPalette")}
  description={$t("commandMenu.commandPaletteDescription")}
  class={cn(
    "bg-popover! text-popover-foreground!",
    "p-1 pt-4!",
    "w-screen! xl:w-7xl! lg:w-5xl! md:w-3xl! sm:w-xl!",
    "rounded-sm! border! shadow-md!",
    "data-[state=open]:animate-in! data-[state=closed]:animate-out! data-[state=closed]:fade-out-0! data-[state=open]:fade-in-0!",
  )}
>
  <Command.Input
    class={cn(
      "border-b! border-border! focus:ring-0! outline-none! mb-2! h-12! px-4!",
      "bg-transparent!",
      "text-sm!",
      "placeholder:text-muted-foreground!",
      "data-[placeholder]:text-center!",
    )}
    placeholder={$t("commandMenu.searchPlaceholder")}
  />
  <Command.List>
    <Command.Empty>{$t("commandMenu.noResults")}</Command.Empty>

    <Command.Group heading={$t("navigation")}>
      {#each navigationLinks as link, i}
        {@const Icon = link.icon}
        <LocalizedLink
          href={link.href}
          class={cn("flex items-center gap-2 w-full data-selected:bg-accent!")}
        >
          <Command.Item
            value={link.label()}
            onSelect={() => navigateTo(link.href)}
            class={cn(
              "flex items-center gap-2 w-full data-selected:bg-accent!",
            )}
          >
            <Icon class={cn("size-4 shrink-0")} />

            <span class="flex-1 text-left">{link.label()}</span>

            <div class={cn("flex items-center gap-1 shrink-0")}>
              {#if isActive($locale as LocaleCode, link.href, link.href2)}
                <Dot size="11px" strokeWidth={2} class="shrink-0 mr-2" />
              {/if}
              <Kbd>{i + 1}</Kbd>
            </div>
          </Command.Item>
        </LocalizedLink>
      {/each}
    </Command.Group>

    <Command.Separator />

    <Command.Group heading={$t("commandMenu.languages")}>
      {#each languages as lang}
        <LocalizedLink
          href={getLocalizedUrl(page.url.pathname, lang.code)}
          class={cn("flex items-center gap-2 w-full data-selected:bg-accent!")}
        >
          <Command.Item
            value={`${lang.name} ${lang.country} ${lang.code}`}
            onSelect={() => selectLanguage(lang)}
            class={cn(
              "flex items-center gap-2 w-full data-selected:bg-accent!",
            )}
          >
            <Globe class={cn("size-4 shrink-0")} />

            <span class="flex-1 text-left">{lang.name} ({lang.country})</span>

            {#if lang.code === $locale}
              <Dot size="11px" strokeWidth={2} class="shrink-0" />
            {/if}
          </Command.Item>
        </LocalizedLink>
      {/each}
    </Command.Group>

    <Command.Separator />

    <Command.Group heading={$t("commandMenu.settings")}>
      <Command.Item
        value={$t("commandMenu.cookieSettings")}
        onSelect={handleCookieSettings}
        class={cn("data-selected:bg-accent!")}
      >
        <Cookie class={cn("me-2 size-4")} />
        <span>{$t("commandMenu.cookieSettings")}</span>
      </Command.Item>
    </Command.Group>

    <Command.Separator />

    <Command.Group heading={$t("commandMenu.misc")}>
      <Command.Item
        value={$t("sidebar.toggleSidebar")}
        onSelect={toggleSidebar}
        class={cn("flex items-center gap-2 w-full data-selected:bg-accent!")}
      >
        <PanelLeft class={cn("h-4 w-4")} />
        <span class={cn("flex-1 text-left")}>
          {$t("sidebar.toggleSidebar")}
        </span>
        <KbdGroup class={cn("ml-auto")}>
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>B</Kbd>
        </KbdGroup>
      </Command.Item>
    </Command.Group>
  </Command.List>
</Command.Dialog>
